import { c as _c } from "react/compiler-runtime";
import { getErrorMessage } from '@onefootprint/fetch-hosted';
import { getLogger, InitShimmer, Logger, logClientVersion, setTenantLogContext, shouldRecordTenantSession, useGetD2PStatus, useGetOnboardingConfig, useParseHandoffUrl, useUpdateD2PStatus } from '@onefootprint/idv';
import type { OnboardingSessionConfig } from '@onefootprint/request-types';
import type { GetD2PResponse } from '@onefootprint/types';
import { D2PStatus, D2PStatusUpdate } from '@onefootprint/types';
import useHandoffMachine from 'src/hooks/use-handoff-machine';
const getSdkVersionQueryParam = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }
  return new URL(window.location.href).searchParams.get('sdkv') || '';
};
const appendLogContext = ({
  meta
}: GetD2PResponse) => {
  Logger.appendGlobalContext({
    fp_session_id: String(meta?.sessionId),
    l10n: JSON.stringify(meta?.l10n),
    opener: String(meta?.opener),
    redirectUrl: String(meta?.redirectUrl)
  });
  logClientVersion(getSdkVersionQueryParam());
};
const setupLogger = (config: OnboardingSessionConfig) => {
  if (shouldRecordTenantSession(config)) {
    setTenantLogContext(config, {
      // @ts-expect-error: browser support
      deviceMemory: typeof navigator?.deviceMemory === 'number' ? navigator.deviceMemory : undefined,
      // @ts-expect-error: browser support
      deviceConnection: typeof navigator?.connection !== 'undefined' ? navigator.connection : undefined,
      isAppClipEnabled: config.isAppClipEnabled,
      isInstantAppEnabled: config.isInstantAppEnabled,
      kind: String(config.kind)
    });
  }
};
const {
  logError,
  logWarn
} = getLogger({
  location: 'handoff-init'
});
const Init = () => {
  const $ = _c(22);
  const [state, send] = useHandoffMachine();
  const {
    authToken: t0
  } = state.context;
  const authToken = t0 === undefined ? "" : t0;
  const updateD2PStatusMutation = useUpdateD2PStatus();
  let t1;
  if ($[0] !== send || $[1] !== state.done) {
    t1 = {
      onSuccess: authTokenFromUrl => {
        if (!state.done) {
          send({
            type: "initContextUpdated",
            payload: {
              authToken: authTokenFromUrl
            }
          });
        }
      },
      onError: _temp
    };
    $[0] = send;
    $[1] = state.done;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useParseHandoffUrl(t1);
  let t2;
  if ($[3] !== authToken || $[4] !== send || $[5] !== state.done || $[6] !== updateD2PStatusMutation) {
    t2 = () => {
      if (!authToken) {
        logError("Found empty auth token while updating d2p");
        return;
      }
      updateD2PStatusMutation.mutate({
        authToken,
        status: D2PStatusUpdate.inProgress
      }, {
        onSuccess() {
          if (!state.done) {
            send({
              type: "initContextUpdated",
              payload: {
                updatedStatus: true
              }
            });
          }
        },
        onError(err) {
          logWarn(`Updating the d2p status to in progress failed: ${getErrorMessage(err)}`, err);
        }
      });
    };
    $[3] = authToken;
    $[4] = send;
    $[5] = state.done;
    $[6] = updateD2PStatusMutation;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const updateD2PStatus = t2;
  const t3 = !state.done;
  let t4;
  if ($[8] !== send || $[9] !== state.done || $[10] !== updateD2PStatus) {
    t4 = {
      onSuccess: data => {
        appendLogContext(data);
        if (!state.done) {
          const {
            meta,
            status
          } = data;
          const opener = meta?.opener ?? "unknown";
          const l10n = meta?.l10n;
          const {
            sandboxIdDocOutcome: idDocOutcome
          } = meta;
          send({
            type: "initContextUpdated",
            payload: {
              opener,
              idDocOutcome,
              l10n
            }
          });
          if (status === D2PStatus.waiting || status === D2PStatus.inProgress) {
            updateD2PStatus();
          } else {
            if (status === D2PStatus.completed || status === D2PStatus.failed) {
              send({
                type: "d2pAlreadyCompleted"
              });
            } else {
              if (status === D2PStatus.canceled) {
                send({
                  type: "d2pCanceled"
                });
              }
            }
          }
        }
      },
      onError: _temp2
    };
    $[8] = send;
    $[9] = state.done;
    $[10] = updateD2PStatus;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== authToken || $[13] !== t3 || $[14] !== t4) {
    t5 = {
      enabled: t3,
      refetchInterval: false,
      authToken,
      options: t4
    };
    $[12] = authToken;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  useGetD2PStatus(t5);
  let t6;
  if ($[16] !== authToken) {
    t6 = {
      authToken
    };
    $[16] = authToken;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== send || $[19] !== state.done) {
    t7 = {
      onSuccess: config => {
        setupLogger(config);
        if (!state.done) {
          send({
            type: "initContextUpdated",
            payload: {
              onboardingConfig: config
            }
          });
        }
      },
      onError: _temp3
    };
    $[18] = send;
    $[19] = state.done;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  useGetOnboardingConfig(t6, t7);
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <InitShimmer />;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};
export default Init;
function _temp() {
  logError("Parsing handoff URL failed on init page");
}
function _temp2(err_0) {
  logWarn(`Fetching d2p status failed on handoff init page: ${getErrorMessage(err_0)}`, err_0);
}
function _temp3(err_1) {
  logWarn(`Fetching onboarding status failed on handoff init page: ${getErrorMessage(err_1)}`, err_1);
}